import React, { useState } from 'react';
import styles from './HomeCard.module.scss';
import SvgWithBranding from 'component/hoc/branding/withBrandingColor.hoc';
import ConfirmationModal from 'component/hoc/success-modal/ConfirmationModal';
import Input from 'component/atom/forms/input/input/Input';
import Modal from 'component/hoc/modal/Modal';
import TextArea from 'component/atom/forms/text-area/TextArea';
import Select from 'component/atom/forms/select/select/Select';
import moment from 'moment';
import { scriptEndpoints } from 'hooks/endpoints';
import { usePost } from 'hooks/usePost';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useUserContext } from 'app-context/user/UserContext';

const HomeCard = ({ item, onEdit, refetch }) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const { mutate: deleteBot, isPending } = usePost({
    onSuccessToast: `${item?.botName} has been successfully deleted`,
    toastType: 'add',
  });
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { selectedBot, setSelectedBot } = useUserContext();

  const handleDelete = () => {
    deleteBot(
      { url: scriptEndpoints?.deleteBot(item.botId) },
      {
        onSuccess: () => {
          setShowDeleteModal(false);
          refetch();
          queryClient.invalidateQueries({ queryKey: ['botsData'] });
          if (selectedBot?.botId === item.botId) {
            setSelectedBot(null);
            localStorage.removeItem('selectedBot');
          }
        },
      }
    );
  };

  return (
    <>
      <div
        className={styles['home-card']}
        onClick={() => {
          const bot = { ...item };
          localStorage.setItem('selectedBot', JSON.stringify(bot));
          setSelectedBot(bot);
          navigate(`/bot/analytics/${item?.botId}`);
        }}
      >
        <div className={styles['card-header']}>
          <div className={styles['logo-container']}>
            <div className={styles['bot']}>
              <div className={styles['bot-default']}>
                <p>
                  {item?.botName
                    ?.split(' ')
                    .map((word) => word.charAt(0))
                    .join('')
                    .slice(0, 3)}
                </p>
              </div>
            </div>
            <div className={styles['title-container']}>
              <span className={styles['title']}>{item?.botName}</span>
              {item?.description && (
                <span className={styles['subtitle']}>{item?.description}</span>
              )}
            </div>
          </div>
          <div className={styles['actions-container']}>
            <div className={styles['action-buttons']}>
              <div className={styles['type']}>
                {0 === item?.botType ? 'Internal' : 'External'}
              </div>
              <SvgWithBranding
                type='edit'
                className={styles['edit-icon']}
                onClick={(e) => {
                  e.stopPropagation();
                  onEdit(item);
                }}
              />
              <SvgWithBranding
                type='delete2'
                className={styles['delete-icon']}
                onClick={(e) => {
                  e.stopPropagation();
                  setShowDeleteModal(true);
                }}
              />
            </div>
            <span className={styles['last-updated']}>
              Last Updated:
              {' ' + moment(item?.lastUpdated).format('DD/MM/YYYY hh:mm A')}
            </span>
          </div>
        </div>
        <div className={styles['card-body']}>
          <div className={styles['conversation-info-container']}>
            <div className={styles['conversation-info']}>
              <p className={styles['info-number']}>{item?.userConversations}</p>
              <p className={styles['info-title']}>User Conversation</p>
            </div>
            <div className={styles['conversation-info']}>
              <p className={styles['info-number']}>{item?.avgLengthHHMMSS}</p>
              <p className={styles['info-title']}>Avg Length</p>
            </div>
          </div>
          <div className={styles['remaining-tokens']}>
            <p className={styles['remaining-tokens-text']}>
              Remaining Tokens: {item?.tokensRemaining} out of{' '}
              {item?.tokenLimit}
            </p>
            <div className={styles['progress-bar']}>
              <div
                className={styles['progress-fill']}
                style={{
                  width: `${Math.min(
                    (item?.tokensRemaining / item?.tokenLimit) * 100,
                    100
                  )}%`,
                }}
              ></div>
            </div>
          </div>

          {item?.topTopics?.length > 0 ? (
            <div className={styles['topics-container']}>
              <div className={styles['topics-header']}>
                <p className={styles['top-topics-text']}>Top Topics</p>
                <p className={styles['total-text']}>Total</p>
              </div>

              <div className={styles['topic-card-container']}>
                {item?.topTopics?.map((topic, index) => {
                  return (
                    <div className={styles['topic-card']} key={index}>
                      <p className={styles['topic-name']}>
                        {topic.categoryText}
                      </p>
                      <p className={styles['total']}>{topic.chatCount}</p>
                    </div>
                  );
                })}
              </div>
            </div>
          ) : null}
        </div>
      </div>
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onSubmit={handleDelete}
        closeOnSubmit={true}
        onApproveButton={{ text: 'Delete' }}
        text='Delete Bot'
        subText={`Are you sure you want to delete <strong>${item?.botName}</strong>?`}
        type='delete2'
        isLoading={isPending}
        subTextIsHtml={true}
      />
      <Modal
        title='Edit Agent'
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        modalContent={{
          component: (
            <div className={styles['form-container']}>
              <Input placeholder='Enter Name' label='Bot Name' />
              <TextArea placeholder='Enter Description' label='Description' />
              <div className={styles['select-container']}>
                <Select label='User Type' />
                <Select label='Bot Type' />
              </div>
            </div>
          ),
        }}
        onSubmit={() => {}}
        onSubmitstyles={{
          onSubmit: {
            label: 'Update',
            type: 'save',
            opicBAckground: true,
          },
        }}
      />
    </>
  );
};

export default HomeCard;
